import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Settings, Package, Users, BarChart3, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import BackButton from '../components/BackButton';
import AdminBillingPlansTab from './AdminBillingPlansTab';
import AdminBillingPackagesTab from './AdminBillingPackagesTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBillingManagement = () => {
  const { getAuthHeader, user } = useAuth();
  const [plans, setPlans] = useState({ member_plans: {}, conseiller_plans: {} });
  const [creditPackages, setCreditPackages] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('plans');
  
  // États pour l'édition
  const [editingPlan, setEditingPlan] = useState(null);
  const [editingPackage, setEditingPackage] = useState(null);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [showPackageForm, setShowPackageForm] = useState(false);

  // Formulaires
  const [planForm, setPlanForm] = useState({
    plan_id: '',
    name: '',
    role: 'member',
    price: 0,
    price_annual: 0,
    monthly_credits: 0,
    zone: 'metropole',
    features: [],
    target: '',
    active: true
  });

  const [packageForm, setPackageForm] = useState({
    package_id: '',
    credits: 0,
    price: 0,
    currency: 'EUR'
  });

  useEffect(() => {
    if (user?.role !== 'super_admin' && user?.role !== 'admin') {
      toast.error('Accès réservé aux administrateurs');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/billing/plans`);
      setPlans({
        member_plans: response.data.member_plans || {},
        conseiller_plans: response.data.conseiller_plans || {}
      });
      setCreditPackages(response.data.credit_packages || {});
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlan = async () => {
    try {
      const endpoint = editingPlan 
        ? `${BACKEND_URL}/api/billing/admin/plans/${editingPlan}`
        : `${BACKEND_URL}/api/billing/admin/plans`;
      
      const method = editingPlan ? 'put' : 'post';
      
      await axios[method](endpoint, planForm, { headers: getAuthHeader() });
      
      toast.success(`Plan ${editingPlan ? 'modifié' : 'créé'} avec succès`);
      setShowPlanForm(false);
      setEditingPlan(null);
      resetPlanForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce plan ?')) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/billing/admin/plans/${planId}`,
        { headers: getAuthHeader() }
      );
      
      toast.success('Plan supprimé avec succès');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleSavePackage = async () => {
    try {
      const endpoint = editingPackage 
        ? `${BACKEND_URL}/api/billing/admin/packages/${editingPackage}`
        : `${BACKEND_URL}/api/billing/admin/packages`;
      
      const method = editingPackage ? 'put' : 'post';
      
      await axios[method](endpoint, packageForm, { headers: getAuthHeader() });
      
      toast.success(`Package ${editingPackage ? 'modifié' : 'créé'} avec succès`);
      setShowPackageForm(false);
      setEditingPackage(null);
      resetPackageForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDeletePackage = async (packageId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce package ?')) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/billing/admin/packages/${packageId}`,
        { headers: getAuthHeader() }
      );
      
      toast.success('Package supprimé avec succès');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const startEditPlan = (planId, plan) => {
    setEditingPlan(planId);
    setPlanForm({
      plan_id: planId,
      name: plan.name,
      role: planId.includes('conseiller') ? 'conseiller' : 'member',
      price: plan.price,
      price_annual: plan.price_annual || plan.price * 10,
      monthly_credits: plan.monthly_credits || 0,
      zone: planId.includes('_dom') ? 'dom' : 'metropole',
      features: plan.features || [],
      target: plan.target || '',
      active: true
    });
    setShowPlanForm(true);
  };

  const startEditPackage = (packageId, pkg) => {
    setEditingPackage(packageId);
    setPackageForm({
      package_id: packageId,
      credits: pkg.credits,
      price: pkg.price,
      currency: pkg.currency || 'EUR'
    });
    setShowPackageForm(true);
  };

  const resetPlanForm = () => {
    setPlanForm({
      plan_id: '',
      name: '',
      role: 'member',
      price: 0,
      price_annual: 0,
      monthly_credits: 0,
      zone: 'metropole',
      features: [],
      target: '',
      active: true
    });
  };

  const resetPackageForm = () => {
    setPackageForm({
      package_id: '',
      credits: 0,
      price: 0,
      currency: 'EUR'
    });
  };

  const addFeature = () => {
    setPlanForm(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const updateFeature = (index, value) => {
    setPlanForm(prev => ({
      ...prev,
      features: prev.features.map((f, i) => i === index ? value : f)
    }));
  };

  const removeFeature = (index) => {
    setPlanForm(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (user?.role !== 'super_admin' && user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Accès Refusé</h2>
            <p className="text-gray-600">Cette page est réservée aux administrateurs</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-6">
      {/* Bouton Retour */}
      <BackButton to="/admin/dashboard" label="Retour" />

      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-3">
            <Settings className="h-8 w-8 text-blue-600" />
            Gestion Modèle Économique
          </h1>
          <p className="text-gray-600 mt-2">
            Administration complète des plans d'abonnement et packages de crédits
          </p>
        </div>
        <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 font-semibold">
          SUPER ADMIN
        </Badge>
      </div>

      {/* Statistiques rapides */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Plans Membres</p>
                <p className="text-3xl font-bold text-blue-600">
                  {Object.keys(plans.member_plans).length}
                </p>
              </div>
              <Users className="h-10 w-10 text-blue-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Plans Conseillers</p>
                <p className="text-3xl font-bold text-green-600">
                  {Object.keys(plans.conseiller_plans).length}
                </p>
              </div>
              <Users className="h-10 w-10 text-green-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Packages Crédits</p>
                <p className="text-3xl font-bold text-purple-600">
                  {Object.keys(creditPackages).length}
                </p>
              </div>
              <Package className="h-10 w-10 text-purple-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Plans</p>
                <p className="text-3xl font-bold text-orange-600">
                  {Object.keys(plans.member_plans).length + Object.keys(plans.conseiller_plans).length}
                </p>
              </div>
              <BarChart3 className="h-10 w-10 text-orange-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Onglets */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="plans">Plans d'Abonnement</TabsTrigger>
          <TabsTrigger value="packages">Packages Crédits</TabsTrigger>
        </TabsList>

        {/* Onglet Plans */}
        <TabsContent value="plans" className="space-y-6 mt-6">
          <AdminBillingPlansTab plans={plans} showPlanForm={showPlanForm} editingPlan={editingPlan} planForm={planForm} setPlanForm={setPlanForm} handleSavePlan={handleSavePlan} setShowPlanForm={setShowPlanForm} setEditingPlan={setEditingPlan} resetPlanForm={resetPlanForm} addFeature={addFeature} updateFeature={updateFeature} removeFeature={removeFeature} startEditPlan={startEditPlan} handleDeletePlan={handleDeletePlan} />
        </TabsContent>

        {/* Onglet Packages */}
        <TabsContent value="packages" className="space-y-6 mt-6">
          <AdminBillingPackagesTab creditPackages={creditPackages} showPackageForm={showPackageForm} editingPackage={editingPackage} packageForm={packageForm} setPackageForm={setPackageForm} handleSavePackage={handleSavePackage} setShowPackageForm={setShowPackageForm} setEditingPackage={setEditingPackage} resetPackageForm={resetPackageForm} startEditPackage={startEditPackage} handleDeletePackage={handleDeletePackage} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminBillingManagement;
